import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_USER_SERVICE } from '@/api/constant';
import Layout from '@/components/Layout/Layout';
import BtnLoader from '@/components/UI/loaders/BtnLoader';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import { IRolesResponse } from '@/interfaces/RolesResponse.interface';
import ErrorMessage from '@/components/common/ErrorMessage';
import { showToastNotification } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { errorTypes } from '@/utility/validation';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

interface StatesProp {
  isLoading: boolean;
  roles: IRolesResponse[];
}

interface FormFieldProps {
  fullName: string;
  loginType: string;
  email: string;
  countryCode: string;
  phone: string;
  password: string;
  roleId: string;
  deviceId: string;
  deviceToken: string;
  status: string;
  isStaff: boolean;
}

const AddStaff = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [states, setStates] = useState<StatesProp>({
    isLoading: false,
    roles: [],
  });
  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormFieldProps>({
    defaultValues: {
      fullName: '',
      email: '',
      loginType: 'email',
      password: 'Nass@123',
      countryCode: '+973',
      phone: '',
      status: '',
      roleId: '',
      deviceId: uuid(),
      deviceToken: uuid(),
      isStaff: true,
    },
  });
  const fetchRoles = async () => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.roles}`;
      const res = await callApi<IRolesResponse[]>(PATH, { method: 'GET' });
      handleChangeState({ roles: res });
    } catch (error) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.log('Error in fetching roles:', error);
    } finally {
      handleChangeState({ isLoading: false });
    }
  };

  const fetchStaff = async () => {
    try {
      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.users}/detail-admin/${id}`;
      const res = await callApi(PATH, { method: 'GET' });
      reset({
        fullName: res.fullName,
        phone: res.phone,
        countryCode: res.countryCode,
        loginType: res.loginType,
        roleId: res.roleId,
        email: res.email,
        status: 'active',
        password: id ? '******' : ''
      });
      console.log('res is', res);
    } catch (error) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.log('Error in fetching staff:', error);
    }
  };

  const onSubmit = async (data: FormFieldProps) => {
    try {
      let postData;
      postData = data
      let PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.auth}/signup`;
      if (id) {
        const { password, ...updatedData } = data;  // Destructure and omit password
        postData = { userId: id, ...updatedData };
        PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.users}/update-profile`;
      }
      await callApi(PATH, { method: id ? 'PUT' : 'POST', data: postData });
      navigate(RoutesLink.staffs);
      console.log('data', data);
    } catch (error) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.log('Error in submitting form:', error);
    }
  };
  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (id) fetchStaff();
  }, [id]);

  return (
    <Layout>
      <Card>
        <div className="flex justify-between">
          <h2 className="text-lg font-semibold mb-4">About Staff</h2>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FormField
                label="Name"
                placeholder="Enter staff name"
                {...register('fullName', { ...errorTypes.required('Name') })}
              />
              <ErrorMessage message={errors.fullName?.message} />
            </div>
            <div>
              <FormField
                label="Email"
                type="email"
                placeholder="Enter email"
                {...register('email', {
                  ...errorTypes.required('email'),
                  ...errorTypes.email,
                })}
              />
              <ErrorMessage message={errors.email?.message} />
            </div>
            <div>
              <FormField
                label="Password"
                placeholder="Enter secure password"
                {...register('password', {
                  ...(id ? {} : errorTypes.required('Password')),
                  ...(id ? {} : errorTypes.password),
                })}
                disabled={id ? true : false}
              />
              <ErrorMessage message={errors.password?.message} />
            </div>
            <div>
              <FormField
                label="Contact"
                prefix="+973"
                placeholder="Enter mobile no."
                {...register('phone', {
                  ...errorTypes.required('Phone number'),
                })}
              />
              <ErrorMessage message={errors.phone?.message} />
            </div>
            <div>
              <FormField
                label="Status"
                type="select"
                placeholder="Select Status"
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                ]}
                {...register('status', { ...errorTypes.required('Status') })}
              />
              <ErrorMessage message={errors.status?.message} />
            </div>
            <div>
              <FormField
                label="Role"
                type="select"
                placeholder="Select Role"
                options={states.roles.map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
                {...register('roleId', { ...errorTypes.required('Role') })}
              />
              <ErrorMessage message={errors.roleId?.message} />
            </div>
          </div>
          <div className="flex justify-end mt-6 gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary"
            >
              {isSubmitting && <BtnLoader />} Save
            </button>
          </div>
        </form>
      </Card>
    </Layout>
  );
};

export default AddStaff;
